import React, { useEffect, useState } from 'react';
import { Product } from '../types';
import { db } from '../utils/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { getWindowOpenAt, getWindowState } from '../utils/productWindows';

interface LatestPageProps {
  onProductClick?: (product: Product) => void;
}

// One product, one full-bleed frame — uk.stussy.com register: the image
// fills the viewport edge to edge, near full height, with only a small
// uppercase caption beneath. No borders, no badges.
const LatestTile: React.FC<{
  product: Product;
  onClick?: (product: Product) => void;
}> = ({ product, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const windowOpenAt = getWindowOpenAt(product);
  const isUpcoming = getWindowState(product) === 'upcoming'
    || (product.status === 'coming_soon' && (!windowOpenAt || new Date(windowOpenAt) > new Date()));

  const opensLine = (() => {
    if (!windowOpenAt) return 'Coming soon';
    const opens = new Date(windowOpenAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `Opens ${opens}`;
  })();

  const hasHoverImage = !!product.hoverImage && product.hoverImage !== product.image;
  // A dedicated landscape crop fills the frame edge to edge on wide screens;
  // without one, the portrait image shows whole, letterboxed on the ground
  const hasWideImage = !!product.wideImage;
  const portraitFit = hasWideImage ? 'landscape:hidden' : 'landscape:object-contain';

  return (
    <div
      className="group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick && onClick(product)}
    >
      <div className="relative w-full h-[85svh] md:h-[92svh] overflow-hidden bg-[#f4f4f4]">
        <img
          src={product.image || '/assets/placeholder.svg'}
          alt={product.title}
          loading="lazy"
          onError={(e) => {
            const t = e.currentTarget;
            if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
          }}
          className={`absolute inset-0 w-full h-full object-cover ${portraitFit} object-center transition-opacity duration-300 ${
            isHovered && hasHoverImage ? 'opacity-0' : 'opacity-100'
          }`}
        />
        {hasHoverImage && (
          <img
            src={product.hoverImage}
            alt={`${product.title} alternate view`}
            loading="lazy"
            onError={(e) => {
              const t = e.currentTarget;
              if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
            }}
            className={`absolute inset-0 w-full h-full object-cover ${portraitFit} object-center transition-opacity duration-300 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}
        {hasWideImage && (
          <img
            src={product.wideImage}
            alt={product.title}
            loading="lazy"
            onError={(e) => {
              const t = e.currentTarget;
              if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
            }}
            className="absolute inset-0 w-full h-full object-cover object-center hidden landscape:block"
          />
        )}
      </div>

      <div className="py-3 flex items-baseline justify-center gap-3">
        <h3 className="font-heading text-[11px] md:text-xs uppercase tracking-widest group-hover:underline underline-offset-2">
          {product.title}
        </h3>
        {isUpcoming ? (
          <span className="font-mono text-[10px] md:text-[11px] text-gray-400 uppercase tracking-widest">
            {opensLine}
          </span>
        ) : (
          <span className="font-mono text-[10px] md:text-[11px] text-gray-500 tracking-widest">
            £{product.price.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
};

// Latest — the newest arrivals, presented uk.stussy.com style: no hero, no
// filters, no sort. Full-size images stacked edge to edge, one per frame.
const LatestPage: React.FC<LatestPageProps> = ({ onProductClick }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatest = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const fetched: Product[] = [];
        const now = new Date();

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.status === 'sold_out' || data.category === 'Vault') return;
          const product = { id: doc.id, ...data } as Product;
          // Closed fragments belong to the Vault; withdrawn artifacts have
          // returned to the archive — neither is a new arrival
          if (getWindowState(product, now) === 'closed' || product.withdrawn) return;
          fetched.push(product);
        });

        // Only the seven newest arrivals across all products — this page
        // is a snapshot of what just surfaced, not a full catalogue
        setProducts(fetched.slice(0, 7));
      } catch (error) {
        console.error('Error fetching latest products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatest();
  }, []);

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Minimal page marker — the images carry the page */}
      <div className="px-3 pt-6 pb-4">
        <h1 className="font-heading text-xs uppercase tracking-[0.3em]">Latest</h1>
      </div>

      {loading ? (
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="min-h-[40vh] flex items-center justify-center px-4 text-center">
          <p className="font-mono text-xs uppercase tracking-widest text-gray-400">
            Nothing new has surfaced yet.
          </p>
        </div>
      ) : (
        <div className="pb-16">
          {products.map(product => (
            <LatestTile key={product.id} product={product} onClick={onProductClick} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LatestPage;
